/**
 * Fonctions de calcul des scores pour les entretiens structurés
 * Extrait de EntStructResults.tsx pour être partagé entre les composants
 */

// ============================================
// TYPES
// ============================================

export interface EntStructCriteriaEtat {
  criteriaId: string;
  description: string;
  points: number;
  covered: boolean;
  asked: boolean;
}

export interface EntStructQuestionEtat {
  questionId: string;
  competence: string;
  competence_weight?: number;
  sous_competence?: string;
  sous_competence_weight?: number;
  question_text: string;
  criteria: EntStructCriteriaEtat[];
}
export interface CritereDetail {
  criteriaId: string;
  description: string;
  points: number;
  covered: boolean;
  asked: boolean;
  questionId: string;
}
export interface SousCompetenceScore {
  nom: string;
  scoreObtenu: number;
  scoreMax: number;
  criteres: CritereDetail[];
}
export interface CompetenceScore {
  nom: string;
  poids: number;
  scoreObtenu: number;
  scoreMax: number;
  scorePondere: number;
  maxPondere: number;
  sousCompetences: SousCompetenceScore[];
  criteres?: CritereDetail[]; // Pour l'ancien format (sans sous-compétences)
}
export interface ScoresCalcules {
  scoreGlobalObtenu: number;
  scoreGlobalMax: number;
  scoreGlobalBrut: number;
  scoreGlobalMaxBrut: number;
  competences: CompetenceScore[];
  isHierarchical: boolean;
}

// ============================================
// FONCTIONS DE CALCUL
// ============================================

/**
 * Arrondit un nombre à 2 décimales
 */
export function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Détecte si les données contiennent des sous-compétences
 */
export function hasSousCompetences(
  questionsEtat: EntStructQuestionEtat[],
): boolean {
  return questionsEtat.some(
    (q) => q.sous_competence && q.sous_competence.trim() !== "",
  );
}

/**
 * Calcule les scores avec structure hiérarchique (compétence > sous-compétences)
 */
export function calculerScoresHierarchique(
  questionsEtat: EntStructQuestionEtat[],
): ScoresCalcules {
  // Map pour grouper par compétence puis par sous-compétence
  const competencesMap = new Map<
    string,
    {
      poids: number;
      sousCompetences: Map<
        string,
        { poids: number; criteres: CritereDetail[] }
      >;
    }
  >();
  const ordreCompetences: string[] = [];

  // Parcourir toutes les questions
  for (const question of questionsEtat) {
    const competence = question.competence;
    const sousCompetence = question.sous_competence || "Général";

    // Initialiser la compétence si nécessaire
    if (!competencesMap.has(competence)) {
      competencesMap.set(competence, {
        poids: question.competence_weight ?? 1,
        sousCompetences: new Map(),
      });
      ordreCompetences.push(competence);
    }

    // Initialiser la sous-compétence si nécessaire
    const compData = competencesMap.get(competence)!;
    if (!compData.sousCompetences.has(sousCompetence)) {
      compData.sousCompetences.set(sousCompetence, {
        poids: question.sous_competence_weight ?? 1,
        criteres: [],
      });
    }

    // Ajouter les critères à la sous-compétence
    const sousCompData = compData.sousCompetences.get(sousCompetence)!;
    for (const critere of question.criteria) {
      sousCompData.criteres.push({
        criteriaId: critere.criteriaId,
        description: critere.description,
        points: critere.points,
        covered: critere.covered,
        asked: critere.asked,
        questionId: question.questionId,
      });
    }
  }

  // Calculer les scores
  const competences: CompetenceScore[] = ordreCompetences.map((nomComp) => {
    const compData = competencesMap.get(nomComp)!;

    // Convertir les sous-compétences en array
    const sousCompetencesArray: SousCompetenceScore[] = [];
    const ordreSousComp = Array.from(compData.sousCompetences.keys());

    for (const nomSousComp of ordreSousComp) {
      const sousCompData = compData.sousCompetences.get(nomSousComp)!;

      const scoreObtenu = sousCompData.criteres
        .filter((c) => c.covered)
        .reduce((sum, c) => sum + c.points, 0);

      const scoreMax = sousCompData.criteres
        .filter((c) => c.points > 0)
        .reduce((sum, c) => sum + c.points, 0);

      sousCompetencesArray.push({
        nom: nomSousComp,
        scoreObtenu,
        scoreMax,
        criteres: sousCompData.criteres,
      });
    }

    // Score de la compétence = somme des scores bruts des sous-compétences
    const scoreObtenu = sousCompetencesArray.reduce(
      (sum, sc) => sum + sc.scoreObtenu,
      0,
    );
    const scoreMax = sousCompetencesArray.reduce(
      (sum, sc) => sum + sc.scoreMax,
      0,
    );
    // Score pondéré de la compétence = score brut × poids de la compétence
    const scorePondere = round2(scoreObtenu * compData.poids);
    const maxPondere = round2(scoreMax * compData.poids);

    return {
      nom: nomComp,
      poids: compData.poids,
      scoreObtenu,
      scoreMax,
      scorePondere,
      maxPondere,
      sousCompetences: sousCompetencesArray,
    };
  });

  // Calculer les scores globaux bruts
  const scoreGlobalBrut = competences.reduce(
    (sum, c) => sum + c.scoreObtenu,
    0,
  );
  const scoreGlobalMaxBrut = competences.reduce(
    (sum, c) => sum + c.scoreMax,
    0,
  );

  // Calculer les scores globaux pondérés (avec poids des compétences)
  // scorePondere inclut déjà le poids de la compétence, on fait simplement la somme
  const scoreGlobalObtenu = round2(
    competences.reduce((sum, c) => sum + c.scorePondere, 0),
  );
  const scoreGlobalMax = round2(
    competences.reduce((sum, c) => sum + c.maxPondere, 0),
  );

  return {
    scoreGlobalObtenu,
    scoreGlobalMax,
    scoreGlobalBrut,
    scoreGlobalMaxBrut,
    competences,
    isHierarchical: true,
  };
}

/**
 * Calcule les scores avec structure plate (ancien format sans sous-compétences)
 */
export function calculerScoresFlat(
  questionsEtat: EntStructQuestionEtat[],
): ScoresCalcules {
  // Map pour grouper les critères et poids par compétence
  const competencesMap = new Map<
    string,
    { criteres: CritereDetail[]; poids: number }
  >();
  const ordreCompetences: string[] = [];

  // Parcourir toutes les questions et leurs critères
  for (const question of questionsEtat) {
    const competence = question.competence;

    // Garder l'ordre d'apparition
    if (!competencesMap.has(competence)) {
      competencesMap.set(competence, {
        criteres: [],
        poids: question.competence_weight ?? 1,
      });
      ordreCompetences.push(competence);
    }

    // Ajouter les critères à la compétence
    const compData = competencesMap.get(competence)!;
    for (const critere of question.criteria) {
      compData.criteres.push({
        criteriaId: critere.criteriaId,
        description: critere.description,
        points: critere.points,
        covered: critere.covered,
        asked: critere.asked,
        questionId: question.questionId,
      });
    }
  }

  // Calculer les scores par compétence avec pondération
  const competences: CompetenceScore[] = ordreCompetences.map((nom) => {
    const { criteres, poids } = competencesMap.get(nom)!;

    // Score obtenu = somme des points où covered=true
    const scoreObtenu = criteres
      .filter((c) => c.covered)
      .reduce((sum, c) => sum + c.points, 0);

    const scoreMax = criteres
      .filter((c) => c.points > 0)
      .reduce((sum, c) => sum + c.points, 0);

    const scorePondere = round2(scoreObtenu * poids);
    const maxPondere = round2(scoreMax * poids);

    return {
      nom,
      poids,
      scoreObtenu,
      scoreMax,
      scorePondere,
      maxPondere,
      sousCompetences: [], // Ancien format sans sous-compétences
      criteres,
    };
  });

  // Calculer les scores globaux bruts
  const scoreGlobalBrut = competences.reduce(
    (sum, c) => sum + c.scoreObtenu,
    0,
  );
  const scoreGlobalMaxBrut = competences.reduce(
    (sum, c) => sum + c.scoreMax,
    0,
  );

  // Calculer les scores globaux pondérés
  const scoreGlobalObtenu = round2(
    competences.reduce((sum, c) => sum + c.scorePondere, 0),
  );
  const scoreGlobalMax = round2(
    competences.reduce((sum, c) => sum + c.maxPondere, 0),
  );

  return {
    scoreGlobalObtenu,
    scoreGlobalMax,
    scoreGlobalBrut,
    scoreGlobalMaxBrut,
    competences,
    isHierarchical: false,
  };
}

/**
 * Fonction principale de calcul des scores
 * Détecte automatiquement le format et utiliser
 */
export function calculerScores(
  questionsEtat: EntStructQuestionEtat[],
): ScoresCalcules {
  const isHierarchical = hasSousCompetences(questionsEtat);

  if (isHierarchical) {
    return calculerScoresHierarchique(questionsEtat);
  }
  return calculerScoresFlat(questionsEtat);
}
